import { useEffect, useRef, useState, useCallback } from 'react';
import { API_BASE_URL } from '../utils/constants';

type ConnectionState = 'connecting' | 'connected' | 'disconnected';

interface SSEEvent {
  status: string;
  payment_id: string;
  verified_by_admin?: boolean;
  user_marked_paid_at?: string | null;
}

export function usePaymentVerificationStream(paymentId: string | null) {
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [lastResult, setLastResult] = useState<SSEEvent | null>(null);
  const [sseReconnectTrigger, setSseReconnectTrigger] = useState(0);
  const xhrRef = useRef<XMLHttpRequest | null>(null);
  const lastEventTimeRef = useRef<number>(Date.now());
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const reconnect = useCallback(() => {
    setConnectionState('connecting');
    setSseReconnectTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!paymentId) {
      setConnectionState('disconnected');
      return;
    }

    const sseUrl = `${API_BASE_URL}/payments/vietqr/status-stream/${paymentId}`;
    let cancelled = false;
    let processedLength = 0;

    const xhr = new XMLHttpRequest();
    xhrRef.current = xhr;
    setConnectionState('connecting');

    xhr.open('GET', sseUrl, true);
    xhr.setRequestHeader('Accept', 'text/event-stream');
    xhr.setRequestHeader('Cache-Control', 'no-cache');

    xhr.onreadystatechange = () => {
      if (xhr.readyState === XMLHttpRequest.HEADERS_RECEIVED) {
        if (!cancelled) {
          setConnectionState('connected');
          lastEventTimeRef.current = Date.now();
        }
      }
    };

    xhr.onprogress = () => {
      if (cancelled) return;
      lastEventTimeRef.current = Date.now();

      const newText = xhr.responseText.substring(processedLength);
      processedLength = xhr.responseText.length;

      const lines = newText.split('\n');
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.status && data.payment_id) {
              setLastResult(data);
              xhr.abort();
              return;
            }
          } catch {
            // ignore parse errors on keepalive comments
          }
        }
      }
    };

    xhr.onerror = () => {
      if (!cancelled) setConnectionState('disconnected');
    };

    xhr.onabort = () => {
      if (!cancelled) setConnectionState('disconnected');
    };

    xhr.onloadend = () => {
      if (!cancelled) setConnectionState('disconnected');
    };

    xhr.send();

    heartbeatIntervalRef.current = setInterval(() => {
      const timeSinceLastEvent = Date.now() - lastEventTimeRef.current;
      if (timeSinceLastEvent > 60000 && !lastResult) {
        setConnectionState('disconnected');
      }
    }, 10000);

    return () => {
      cancelled = true;
      xhr.abort();
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [paymentId, sseReconnectTrigger, lastResult]);

  return { connectionState, lastResult, reconnect };
}
